// Shared helpers for grouping repeated same-day focus sessions logged against
// the same habit/task/to-do (or the same freeform typed label when there's no
// linked id) into one "total duration + individual logs" view.

export function sessionsForItemOnDate(focusSessions, { kind, id, label }, date) {
  const trimmedLabel = (label || "").trim();
  return focusSessions
    .filter(s => s.date === date && (
      id != null ? (s.refId === id && s.kind === kind) : (!s.refId && (s.label || "").trim() === trimmedLabel && trimmedLabel !== "")
    ))
    .slice()
    .sort((a, b) => (a.startedAt || a.completedAt || "").localeCompare(b.startedAt || b.completedAt || ""));
}

export const ORDINALS = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];

export const logOrdinalLabel = (i) => ORDINALS[i] || `${i + 1}th`;

export const fmtClockTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  : null;

export const logTimeRangeLabel = (s) => {
  // Logged by length only ("+ Add past session" > Duration): it has no real
  // start/end, so there is no time to show (the details panel hides the row).
  if (s.estimated && s.minutes >= 1) return "";
  const start = fmtClockTime(s.startedAt);
  const end = fmtClockTime(s.completedAt);
  return start && end ? `${start} – ${end}` : (end || start || "");
};

// Groups a flat list of sessions into one entry per (item, calendar day) —
// so a habit/task/to-do logged twice on the same day shows as a single row
// with a combined total, while the same item on a different day stays its
// own separate row.
export function groupSessionsByItemAndDay(sessions) {
  const map = new Map();
  sessions.forEach(s => {
    const itemKey = s.refId != null ? `${s.kind}:${s.refId}` : `label:${(s.label || "").trim()}`;
    const key = `${itemKey}__${s.date}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  });
  return [...map.values()].map(group => {
    const sessionsAsc = group.slice().sort((a, b) => (a.startedAt || a.completedAt || "").localeCompare(b.startedAt || b.completedAt || ""));
    const representative = group.slice().sort((a, b) => (b.completedAt || b.startedAt || b.date || "").localeCompare(a.completedAt || a.startedAt || a.date || ""))[0];
    const totalMinutes = group.reduce((a, s) => a + s.minutes, 0);
    return { sessions: sessionsAsc, representative, totalMinutes, count: group.length };
  });
}

// ─── Session notes (Jade, 2026-10-10) ──────────────────────────────────────
// A logged session can carry a free-text `note` ("finished chapter 3",
// "invoice: client X"). It lives on the session object itself, so it syncs
// with clarity_focusSessions and is deleted with the session.
export const SESSION_NOTE_MAX = 500;

// Same logged session? Newer sessions have an id; older ones don't, so they
// fall back to their timestamps + label (unique enough: two sessions can't
// complete at the same millisecond with the same label).
export function sameSession(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.id || b.id) return !!a.id && a.id === b.id;
  return a.completedAt === b.completedAt && a.startedAt === b.startedAt && (a.label || "") === (b.label || "");
}

// The sessions array with `target`'s note set (trimmed; empty removes it).
export function withSessionNote(sessions, target, note) {
  const text = String(note || "").trim().slice(0, SESSION_NOTE_MAX);
  return sessions.map(s => {
    if (!sameSession(s, target)) return s;
    if (text) return { ...s, note: text };
    const { note: _drop, ...rest } = s;
    return rest;
  });
}

export const hasSessionNote = (s) => !!(s && typeof s.note === "string" && s.note.trim());
